<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-button @click.native="onShowVatModal">Create</app-button>
      <app-cells><h2 class="title">Pending</h2></app-cells>
      <table-list-vat-pending
        @clickPagination="onClickPaginationPending"
        :table-items="table_pending_items"
        :table-preloader="table_pending_preloader"
        :pagination="pagination_pending"
        :date-format-month="dateFormatMonth"
        :amount-format="amountFormat"
      />
      <app-cells><h2 class="title">Completed</h2></app-cells>
      <table-list-vat-completed
        @clickPagination="onClickPaginationCompleted"
        :table-items="table_completed_items"
        :table-preloader="table_completed_preloader"
        :pagination="pagination_completed"
        :date-format-month="dateFormatMonth"
        :amount-format="amountFormat"
      />
    </template>
    <modal
      name="vat_modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('vat_modal')"></button>
        <div class="popup__title">Create VAT statement</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Legal entity" required>
              <app-select
                v-model="form.legal_entity"
                :options="legal_entity_options"
                :reduce="item => item.code"
                :clearable="false"
                :searchable="false"
                label="short_name"
                placeholder="Legal entity"
                :error="$v.form.legal_entity.$error"
              />
              <template #error>
                <div v-if="$v.form.legal_entity.$dirty && !$v.form.legal_entity.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Month" required>
              <app-date-picker
                v-model="form.date"
                format="MMM YYYY"
                type="month"
                placeholder="Month"
                :error="$v.form.date.$error"
              />
              <template #error>
                <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="submitVat">Create</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getAdministrativeLegalEntities,
  getFinanceVatStatementsList,
  postFinanceVatStatements
} from '@/http'
import { required } from 'vuelidate/lib/validators'
import { format, parse } from 'date-fns'
import TableListVatPending from '@/views/finance/taxes/vat/tables/TableListVatPending'
import TableListVatCompleted from '@/views/finance/taxes/vat/tables/TableListVatCompleted'

export default {
  name: 'FinanceTaxesVATStatement',
  components: {TableListVatPending, TableListVatCompleted},
  data() {
    return {
      page_loaded: false,
      form: {},
      legal_entity_options: [],
      table_pending_items: [],
      pagination_pending: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_pending_preloader: false,
      table_completed_items: [],
      pagination_completed: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_completed_preloader: false,
    }
  },
  validations: {
    form: {
      date: { required },
      legal_entity: { required },
    }
  },
  created() {
    Promise.all([
      getAdministrativeLegalEntities(),
      this.fetchItemsPending(),
      this.fetchItemsCompleted(),
    ])
      .then(response => {
        this.legal_entity_options = response[0].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItemsPending() {
      this.table_pending_preloader = true
      const response = await getFinanceVatStatementsList({
        completed: false,
        page: this.pagination_pending.page,
        page_size: 20,
        ordering: 'to_date',
      })
      this.table_pending_items = response.data.results
      this.pagination_pending.pages = response.data.pages
      this.pagination_pending.count = response.data.count
      this.table_pending_preloader = false
    },
    async fetchItemsCompleted() {
      this.table_completed_preloader = true
      const response = await getFinanceVatStatementsList({
        completed: true,
        page: this.pagination_completed.page,
        page_size: 20,
        ordering: '-to_date',
      })
      this.table_completed_items = response.data.results
      this.pagination_completed.pages = response.data.pages
      this.pagination_completed.count = response.data.count
      this.table_completed_preloader = false
    },
    onClickPaginationPending(page) {
      this.pagination_pending.page = page
      this.fetchItemsPending()
    },
    onClickPaginationCompleted(page) {
      this.pagination_completed.page = page
      this.fetchItemsCompleted()
    },
    onShowVatModal() {
      this.form = {}
      this.$v.form.$reset()
      this.$modal.show('vat_modal')
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submitVat.preload = true
      postFinanceVatStatements(this.form)
        .then(() => {
          this.fetchItemsPending()
          this.$notify({
            type: 'success',
            title: 'VAT statement added',
          })
          this.$modal.hide('vat_modal')
        })
        .catch(() => { this.$refs.submitVat.preload = false })
    },
    dateFormatMonth(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy') : '-'
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
  }
}
</script>